import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import MobileIdentifier from '../../../atoms/mobile-identifier/MobileIdentifier';
import InfiniteScroll from 'react-infinite-scroll-component';
import CommentCard from '../../../molecules/comments/comment-card/CommentCard';
import LongCard from '../../../molecules/comments/comment-card/LongCard';
import styles from './styles.scss';
import Utils from '../../../../utils/comments-utils';
import ErrorBoundary from '../../../atoms/error-boundary';
import CommentViewHeader from '../../../molecules/comments/comment-view-header';
import { CommentViewProps, CommentPageData, Comment } from '../../../../utils/comments/types'
// import Spinner from '../../../molecules/spinner/Spinner';

const CommentPage = function CommentPage(
    { targetkey, isMobile = false, appealurl, appealname, favicon, charitykey }: CommentViewProps
) {
    // states for fetching comments
    const [sortType, setSortType] = useState(Utils.SortOrders.Newest.Key);
    const [commentData, setCommentData] = useState<CommentPageData>({
        fetchedCount: 0,
        list: [],
        count: 0,
    });
    const [replies, setReplies] = useState<Array<Comment>>([]);

    // infinte scrolling/Pagination states
    const fetchCountInterval = 48; //divisible by 1, 2 and 3
    const [fetchedCommentNum, setFetchedCommentNum] = useState(fetchCountInterval);

    const fetchComments = async () => {
        try {
            const toSkip = fetchedCommentNum - fetchCountInterval;

            const filter = `querykey eq '${targetkey}' and(parent eq '')`;
            const apiRequestUrl = `/odata/messages?$apply=filter(${filter})&$count=true&$orderby=${Utils.GetOrderByQuery(sortType)}&$skip=${toSkip}&$top=${fetchCountInterval}`;

            const response = await fetch(apiRequestUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch comments');
            }

            const json = await response.json();
            const newComments = Utils.GetNestedComments(charitykey, json.value, appealname);

            // Check to see if sort type has been changed
            // If so, start a new list, otherwise append to existing
            const commentsToShow = fetchedCommentNum === fetchCountInterval
                ? newComments
                : [...new Set(commentData.list.concat(newComments))]; // Set object to only contain distinct values

            return {
                fetchedCount: newComments.length,
                list: commentsToShow,
                count: json['@odata.count'],
            };
        } catch (e) {
            console.error(e);
            return commentData;
        }
    };

    const fetchReplies = async () => {
        try {
            const parents = commentData.list.map((c) => c.Key).join('\',\'');

            if (parents.length <= 0) {
                return [];
            }

            const replyRequestUrl = `/odata/messages?$apply=filter(parent in ('${parents}'))&$top=${fetchCountInterval}`;

            const response = await fetch(replyRequestUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch replies');
            }

            const json = await response.json();
            const newReplies = json.value;

            // Check to see if sort type has been changed
            // If so, start a new list, otherwise append to existing
            const repliesToShow = fetchedCommentNum === fetchCountInterval
                ? newReplies
                : [...new Set(replies.concat(newReplies))]; // Set object to only contain distinct values

            return repliesToShow;

        } catch (e) {
            console.error(e);
            return replies;
        }
    };

    useEffect(() => {
        const fetch = async () => setCommentData(await fetchComments());

        fetch();
    }, [fetchedCommentNum, sortType]);

    // fetch replies only after comments are fetch - improves load time feedback
    useEffect(() => {
        const fetch = async () => setReplies(await fetchReplies());

        fetch();
    }, [commentData]);

    const hasMore = commentData.fetchedCount >= fetchCountInterval;

    // on infinite scroll Pagination
    const fetchMoreData = async () => {
        if (hasMore) {
            await setCommentData(await fetchComments());
            setFetchedCommentNum(fetchedCommentNum + fetchCountInterval);
        }
    };

    return (
        <ErrorBoundary>
            <div className={styles['comment-page']}>
                <Grid container spacing={3}>
                    <Grid item xs={12}> {/*className={`${styleOverride}`}>*/}
                        <CommentViewHeader
                            commentCount={commentData.count}
                            sortType={sortType}
                            sortTypeCallback={(value) => {
                                setSortType(value);

                                // set the state of pagination back to initial state or we get a mix of sortType filtered comments.
                                // setFetchCommentNum because of the stateful implication or -> initial load, ran out of comments to fetch etc...
                                setFetchedCommentNum(fetchCountInterval);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <div className={styles['comment-container']}>
                            {commentData.list.length > 0 && (
                                <InfiniteScroll
                                    className={styles['infinite-scroll']}
                                    dataLength={commentData.list.length}
                                    next={fetchMoreData}
                                    hasMore={hasMore}
                                    loader={(
                                        //<Grid item xs={12}><Spinner /></Grid>
                                        <Grid item xs={12} /> // we could add a spinner?
                                    )}
                                >
                                    <Grid container spacing={2}>
                                        {commentData.list.map((c: Comment) => (
                                            <Grid item xs={12} md={6} lg={4} key={c.Key}>
                                                {
                                                    isMobile
                                                        ? (
                                                            <LongCard
                                                                pageUrl={Utils.GetPageUrl(c.GroupPath, appealurl)}
                                                                targetName={Utils.GetTargetName(charitykey, c.TargetName, appealname)}
                                                                avatarSrc={favicon}
                                                                comment={{
                                                                    ...c,
                                                                    Reply: replies.find((r) => r.Parent === c.Key),
                                                                }}
                                                            />
                                                        )
                                                        : (
                                                            <CommentCard
                                                                pageUrl={Utils.GetPageUrl(c.GroupPath, appealurl)}
                                                                targetName={Utils.GetTargetName(charitykey, c.TargetName, appealname)}
                                                                avatarSrc={favicon}
                                                                comment={{
                                                                    ...c,
                                                                    Reply: replies.find((r) => r.Parent === c.Key),
                                                                }}
                                                                isMobile={isMobile}

                                                            />
                                                        )
                                                }
                                            </Grid>
                                        ))}
                                    </Grid>
                                </InfiniteScroll>
                            )}
                        </div >
                    </Grid>
                </Grid>
            </div>
        </ErrorBoundary>
    );
};

export default MobileIdentifier(CommentPage);
